import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useMergeLink } from '@mergeapi/react-merge-link';
import {
  Link2, RefreshCw, CheckCircle, XCircle, AlertCircle,
  Loader2, Users, Clock, Database, Zap, ArrowRight, Unplug, Shield
} from 'lucide-react';
import { COLORS } from '../../../constants/dashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRIntegrationsView = ({ user, businessProfile }) => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const isAdmin = user?.role === 'business_admin' || user?.role === 'super_admin';
  const isEnterprise = ['enterprise', 'Enterprise'].includes(
    config?.tier || businessProfile?.subscription_tier
  );

  /* ---- Data fetching ---- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, historyRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/hr/config`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/hr/sync/history`, { withCredentials: true }),
      ]);
      setConfig(configRes.data);
      setSyncHistory(historyRes.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch HR config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ---- Merge Link flow ---- */
  const fetchLinkToken = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/hr/link-token`, {}, { withCredentials: true });
      setLinkToken(res.data.link_token);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to start connection';
      alert(msg);
      setConnecting(false);
    }
  };

  const onMergeSuccess = useCallback(async (publicToken) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/hr/account-token`,
        { public_token: publicToken },
        { withCredentials: true },
      );
      fetchData();
    } catch (err) {
      alert('Failed to connect HR system. Please try again.');
    } finally {
      setConnecting(false);
      setLinkToken(null);
    }
  }, [fetchData]);

  const onMergeExit = useCallback(() => {
    setConnecting(false);
    setLinkToken(null);
  }, []);

  const { open, isReady } = useMergeLink({
    linkToken: linkToken || '',
    onSuccess: onMergeSuccess,
    onExit: onMergeExit,
  });

  // Auto-open Merge Link once token is ready
  useEffect(() => {
    if (linkToken && isReady) open();
  }, [linkToken, isReady, open]);

  /* ---- Sync ---- */
  const triggerSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/hr/sync`, {}, { withCredentials: true });
      // Wait a bit for background task, then refresh
      setTimeout(() => fetchData(), 3000);
    } catch (err) {
      alert('Sync failed: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setSyncing(false);
    }
  };

  /* ---- Disconnect ---- */
  const disconnectHR = async () => {
    if (!window.confirm('Disconnect your HR system? Synced employee data will remain.')) return;
    setDisconnecting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/hr/disconnect`, {}, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  /* ---- Status helpers ---- */
  const statusIcon = (status) => {
    if (status === 'success') return <CheckCircle className="w-4 h-4" style={{ color: COLORS.success }} />;
    if (status === 'failed') return <XCircle className="w-4 h-4" style={{ color: COLORS.danger }} />;
    if (status === 'in_progress') return <Loader2 className="w-4 h-4 animate-spin" style={{ color: COLORS.warning }} />;
    return <AlertCircle className="w-4 h-4 text-gray-400" />;
  };

  /* ---- Loading ---- */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="hr-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  /* ---- Enterprise Gate ---- */
  if (!isEnterprise) {
    return (
      <div className="p-4" data-testid="hr-upgrade-prompt">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center max-w-lg mx-auto">
          <div className="w-14 h-14 rounded-xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">HR System Integration</h3>
          <p className="text-sm text-gray-500 leading-relaxed mb-2">
            Automatically sync employees from your HR system — Workday, BambooHR, ADP, Gusto, Rippling, and 50+ more — with a single connection.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Available exclusively on the <span className="font-semibold text-gray-700">Enterprise plan ($299/mo)</span>.
          </p>
          <button
            data-testid="hr-upgrade-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'billing' }))}
            className="inline-flex items-center gap-2 bg-gray-900 text-white font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-gray-800 transition-all"
          >
            Upgrade to Enterprise
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const isConnected = config?.hr_system === 'merge' && config?.enabled;

  return (
    <div className="p-4" style={{ background: COLORS.background }} data-testid="hr-integrations-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: COLORS.primary }}>HR System Integration</h2>
          <p className="text-sm text-gray-500">
            Connect your HR system via Merge to automatically sync employee data
          </p>
        </div>
        {isConnected && (
          <div className="flex items-center gap-2">
            <button
              data-testid="hr-sync-btn"
              onClick={triggerSync}
              disabled={syncing}
              className="px-4 py-2 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 text-sm"
              style={{ background: COLORS.primary }}
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync Now
            </button>
            <button
              data-testid="hr-refresh-btn"
              onClick={fetchData}
              className="p-2 bg-white rounded-lg border shadow-sm hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        )}
      </div>

      {/* Connected State */}
      {isConnected ? (
        <div className="bg-white rounded-xl border shadow-sm mb-6 overflow-hidden" data-testid="hr-connected-card">
          <div className="p-4 border-b flex items-center justify-between" style={{ background: `${COLORS.success}10` }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white border">
                <Link2 className="w-6 h-6" style={{ color: COLORS.success }} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  {config.merge_integration_name || 'HR System'}
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium text-white" style={{ background: COLORS.success }}>
                    Connected
                  </span>
                </h3>
                <p className="text-sm text-gray-500">Connected via Merge unified API</p>
              </div>
            </div>
            {isAdmin && (
              <button
                data-testid="hr-disconnect-btn"
                onClick={disconnectHR}
                disabled={disconnecting}
                className="px-3 py-1.5 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-50 flex items-center gap-1.5 disabled:opacity-50"
              >
                {disconnecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                Disconnect
              </button>
            )}
          </div>
          <div className="p-4 grid grid-cols-4 gap-4">
            <div className="text-center">
              <Users className="w-6 h-6 mx-auto mb-1" style={{ color: COLORS.primary }} />
              <p className="text-xl font-bold text-gray-900">{config.employees_synced || 0}</p>
              <p className="text-xs text-gray-500">Employees Synced</p>
            </div>
            <div className="text-center">
              <Clock className="w-6 h-6 mx-auto mb-1" style={{ color: COLORS.warning }} />
              <p className="text-sm font-bold text-gray-900">
                {config.last_sync_at ? new Date(config.last_sync_at).toLocaleDateString() : 'Never'}
              </p>
              <p className="text-xs text-gray-500">Last Sync</p>
            </div>
            <div className="text-center">
              <RefreshCw className="w-6 h-6 mx-auto mb-1" style={{ color: COLORS.accent }} />
              <p className="text-sm font-bold text-gray-900 capitalize">{config.sync_frequency || 'Manual'}</p>
              <p className="text-xs text-gray-500">Sync Frequency</p>
            </div>
            <div className="text-center">
              {statusIcon(config.last_sync_status)}
              <p className="text-sm font-bold text-gray-900 capitalize mt-1">{config.last_sync_status || 'Unknown'}</p>
              <p className="text-xs text-gray-500">Sync Status</p>
            </div>
          </div>
          {config.last_sync_message && (
            <div className="px-4 pb-4">
              <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2">{config.last_sync_message}</p>
            </div>
          )}
        </div>
      ) : (
        /* Not connected — show connect CTA */
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-8 mb-6 text-center" data-testid="hr-connect-prompt">
          <Link2 className="w-12 h-12 mx-auto mb-3" style={{ color: COLORS.primary }} />
          <h3 className="text-lg font-bold text-gray-900 mb-2">Connect Your HR System</h3>
          <p className="text-gray-600 mb-2 text-sm max-w-md mx-auto">
            Use Merge to securely connect Workday, BambooHR, ADP, Gusto, Rippling, and 50+ other HR platforms with a single click.
          </p>
          <p className="text-xs text-gray-500 mb-5">Your credentials are handled securely by Merge and never stored on our servers.</p>
          {isAdmin ? (
            <button
              data-testid="hr-connect-btn"
              onClick={fetchLinkToken}
              disabled={connecting}
              className="inline-flex items-center gap-2 text-white font-semibold px-6 py-2.5 rounded-lg text-sm disabled:opacity-50 transition-all"
              style={{ background: COLORS.primary }}
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {connecting ? 'Opening Merge...' : 'Connect HR System'}
            </button>
          ) : (
            <p className="text-sm text-gray-500">Ask your company admin to connect the HR system.</p>
          )}
        </div>
      )}

      {/* Supported Integrations */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden mb-6">
        <div className="p-4 border-b" style={{ background: `${COLORS.primary}05` }}>
          <h3 className="font-bold text-sm" style={{ color: COLORS.primary }}>Supported HR Platforms</h3>
          <p className="text-xs text-gray-500">50+ platforms available through Merge</p>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-2">
            {['Workday', 'BambooHR', 'ADP', 'Gusto', 'Rippling', 'Namely', 'Paylocity', 'Paycom', 'UKG', 'Ceridian', 'SAP SuccessFactors', 'Oracle HCM', 'Zenefits', 'Paychex', 'Trinet'].map(name => (
              <span key={name} className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium text-gray-700">
                {name}
              </span>
            ))}
            <span className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs font-medium text-blue-700">
              + 40 more
            </span>
          </div>
        </div>
      </div>

      {/* Sync History */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="hr-sync-history">
        <div className="p-4 border-b" style={{ background: `${COLORS.primary}05` }}>
          <h3 className="font-bold text-sm" style={{ color: COLORS.primary }}>Sync History</h3>
          <p className="text-xs text-gray-500">Recent synchronization activity</p>
        </div>
        {syncHistory.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Database className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No sync history yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {syncHistory.map((sync, idx) => (
              <div key={sync.sync_log_id || idx} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100">
                    {statusIcon(sync.status)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{sync.hr_system || 'Merge'} Sync</p>
                    <p className="text-xs text-gray-500">
                      {sync.employees_created || 0} new, {sync.employees_updated || 0} updated
                      {sync.errors?.length > 0 && <span className="text-red-500 ml-1">({sync.errors.length} error{sync.errors.length > 1 ? 's' : ''})</span>}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">
                    {new Date(sync.started_at).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(sync.started_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HRIntegrationsView;
